// src/utils/ledger/generateStandardLedger.js
// ============================================================
//  STANDARD TIER — Internal Advance Ledger Report
// ============================================================
import jsPDF from 'jspdf';
import {
  BRAND, rgb, fmtINR, fmtDate, fmtDateTime,
  STATUS_STYLES, computeSummary, groupByClient, groupByMode,
  computeAging, roundRectFill, roundRectStroke,
  LEDGER_LAYOUT, stampAllPages,
} from './shared';
import { PdfLayoutEngine, TABLE_METRICS } from './pdfLayoutEngine';

const drawHeaderFooter = (doc, page, totalPages) => {
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();

  doc.setFillColor(...rgb(BRAND.GOLD));
  doc.rect(0, 0, w, LEDGER_LAYOUT.HEADER_H, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.text('DEVELOPMENT EXPRESS', 15, 9);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.text('Development Express  ·  Construction Machinery Rental & Monitoring', 15, 14);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.text('INTERNAL  ·  CONFIDENTIAL', w - 15, 9, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.text('Advance Ledger Report', w - 15, 14, { align: 'right' });

  doc.setDrawColor(...rgb(BRAND.GOLD));
  doc.setLineWidth(0.4);
  doc.line(15, h - LEDGER_LAYOUT.FOOTER_H, w - 15, h - LEDGER_LAYOUT.FOOTER_H);

  doc.setTextColor(...rgb(BRAND.MUTED));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.text(
    'Generated by Development Express  ·  developmentexpress.in  ·  Karad, Maharashtra',
    15, h - 9,
  );
  doc.text(`Page ${page} of ${totalPages}`, w - 15, h - 9, { align: 'right' });

  doc.setFillColor(...rgb(BRAND.GOLD));
  doc.circle(w - 30, h - 9.5, 0.8, 'F');
};

const drawSectionTitle = (doc, y, num, title) => {
  doc.setTextColor(...rgb(BRAND.GOLD_DARK));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text(`${num}.  ${title}`, LEDGER_LAYOUT.SIDE, y);
  return y + TABLE_METRICS.SECTION_HEADER;
};

const drawKPICards = (doc, startY, summary) => {
  const margin = LEDGER_LAYOUT.SIDE;
  const w = doc.internal.pageSize.getWidth() - margin * 2;
  const cardW = (w - 9) / 4;
  const cardH = 24;
  const cards = [
    { label: 'Total Advance Received', value: fmtINR(summary.received),
      sub: 'Period total', accent: BRAND.GOLD },
    { label: 'Adjusted Against Invoices', value: fmtINR(summary.adjusted),
      sub: summary.received > 0
        ? `${((summary.adjusted / summary.received) * 100).toFixed(1)}% of received`
        : '—',
      accent: BRAND.EMERALD },
    { label: 'Refunded to Clients', value: fmtINR(summary.refunded),
      sub: 'Period refunds', accent: BRAND.SAPPHIRE },
    { label: 'Outstanding Balance', value: fmtINR(summary.outstanding),
      sub: 'Carry-forward', accent: BRAND.RUBY },
  ];

  cards.forEach((c, i) => {
    const x = margin + i * (cardW + 3);
    roundRectFill(doc, x, startY, cardW, cardH, 1, '#FFFFFF');
    roundRectStroke(doc, x, startY, cardW, cardH, 1, BRAND.BORDER, 0.2);
    doc.setFillColor(...rgb(c.accent));
    doc.rect(x, startY, cardW, 1, 'F');
    doc.setTextColor(...rgb(BRAND.MUTED));
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.text(c.label.toUpperCase(), x + 3, startY + 5);
    doc.setTextColor(...rgb(BRAND.INK));
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(c.value, x + 3, startY + 13);
    doc.setTextColor(...rgb(BRAND.MUTED));
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.text(c.sub, x + 3, startY + 20);
  });

  return startY + cardH + TABLE_METRICS.GAP_AFTER_SECTION;
};

const goldTableHead = {
  fillColor: rgb(BRAND.GOLD),
  textColor: [255, 255, 255],
  fontStyle: 'bold',
  cellPadding: 2,
};

export const generateStandardLedger = async (reportData) => {
  const {
    period_start, period_end, generated_at, report_id,
    transactions = [],
    debugPagination,
  } = reportData;

  const summary = computeSummary(transactions);
  const clients = groupByClient(transactions);
  const modes = groupByMode(transactions);
  const aging = computeAging(transactions);

  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const layout = new PdfLayoutEngine(doc, LEDGER_LAYOUT, {
    debug: debugPagination ?? undefined,
    label: `standard-ledger:${report_id}`,
  });

  // ----- Cover block (page 1) -----
  layout.drawBlock(28, 'report-title', (pdf, y) => {
    pdf.setTextColor(...rgb(BRAND.INK));
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(17);
    pdf.text('Internal Advance Ledger Report', LEDGER_LAYOUT.SIDE, y);
    pdf.setTextColor(...rgb(BRAND.MUTED));
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    const meta = `Period: ${fmtDate(period_start)} - ${fmtDate(period_end)}  ·  `
      + `Generated: ${fmtDateTime(generated_at)}  ·  Report ID: ${report_id}`;
    pdf.text(meta, LEDGER_LAYOUT.SIDE, y + 6);
    return y + 12;
  });

  layout.drawBlock(TABLE_METRICS.KPI_BLOCK, 'kpi-cards', (pdf, y) =>
    drawKPICards(pdf, y, summary));

  const clientRows = clients.map((c) => [
    c.client_name,
    c.project_site,
    fmtINR(c.received),
    fmtINR(c.adjusted),
    fmtINR(c.refunded),
    fmtINR(c.balance),
    c.status,
  ]);
  clientRows.push([
    'TOTAL', '',
    fmtINR(summary.received),
    fmtINR(summary.adjusted),
    fmtINR(summary.refunded),
    fmtINR(summary.outstanding),
    '',
  ]);

  layout.drawSectionTable({
    sectionId: 'client-summary',
    drawTitleFn: (pdf, cursor) => drawSectionTitle(pdf, cursor, 1, 'Client-wise Advance Summary'),
    rowCount: clientRows.length,
    tableOptions: {
      head: [['Client', 'Project Site', 'Received', 'Adjusted', 'Refunded', 'Balance', 'Status']],
      body: clientRows,
      theme: 'grid',
      headStyles: { ...goldTableHead, fontSize: 8, halign: 'left' },
      bodyStyles: { fontSize: 7.5, textColor: rgb(BRAND.INK), cellPadding: 2 },
      alternateRowStyles: { fillColor: rgb(BRAND.ROW_ALT) },
      columnStyles: {
        0: { cellWidth: 38 },
        1: { cellWidth: 36 },
        2: { halign: 'right', cellWidth: 22 },
        3: { halign: 'right', cellWidth: 22 },
        4: { halign: 'right', cellWidth: 21 },
        5: { halign: 'right', cellWidth: 22 },
        6: { halign: 'center', cellWidth: 19, fontStyle: 'bold' },
      },
      didParseCell: (data) => {
        if (data.row.index === clientRows.length - 1) {
          data.cell.styles.fillColor = rgb(BRAND.GOLD_LIGHT);
          data.cell.styles.textColor = rgb(BRAND.GOLD_DEEP);
          data.cell.styles.fontStyle = 'bold';
        }
        if (data.column.index === 6 && data.section === 'body'
            && data.row.index < clientRows.length - 1) {
          const status = data.cell.raw;
          if (STATUS_STYLES[status]) {
            data.cell.styles.textColor = rgb(STATUS_STYLES[status].fg);
          }
        }
      },
    },
  });

  const txnRows = transactions.map((t) => [
    fmtDate(t.txn_date, true),
    t.receipt_no,
    `${t.client_name}  ·  ${t.machine_code || ''}`,
    t.payment_mode || '—',
    t.type,
    t.type === 'RECEIPT' ? fmtINR(t.amount) : `(${fmtINR(t.amount)})`,
    t.reference || '—',
  ]);

  layout.drawSectionTable({
    sectionId: 'transactions',
    drawTitleFn: (pdf, cursor) =>
      drawSectionTitle(pdf, cursor, 2, `Detailed Transactions (${transactions.length} entries)`),
    rowCount: txnRows.length,
    rowHeight: TABLE_METRICS.ROW_COMPACT,
    minKeepRows: 2,
    tableOptions: {
      head: [['Date', 'Receipt #', 'Client / Machine', 'Mode', 'Type', 'Amount', 'Reference']],
      body: txnRows,
      theme: 'grid',
      rowPageBreak: txnRows.length > 10 ? 'auto' : 'avoid',
      headStyles: { ...goldTableHead, fontSize: 7.5 },
      bodyStyles: { fontSize: 7, textColor: rgb(BRAND.INK), cellPadding: 1.8 },
      alternateRowStyles: { fillColor: rgb(BRAND.ROW_ALT) },
      columnStyles: {
        0: { cellWidth: 16 },
        1: { cellWidth: 24, fontSize: 6.5 },
        2: { cellWidth: 46 },
        3: { halign: 'center', cellWidth: 14 },
        4: { halign: 'center', cellWidth: 20, fontStyle: 'bold' },
        5: { halign: 'right', cellWidth: 24 },
        6: { cellWidth: 36, fontSize: 6.5 },
      },
      didParseCell: (data) => {
        if (data.section === 'body') {
          if (data.column.index === 4 && STATUS_STYLES[data.cell.raw]) {
            data.cell.styles.textColor = rgb(STATUS_STYLES[data.cell.raw].fg);
          }
          if (data.column.index === 5 && STATUS_STYLES[data.row.raw[4]]) {
            data.cell.styles.textColor = rgb(STATUS_STYLES[data.row.raw[4]].fg);
            data.cell.styles.fontStyle = 'bold';
          }
        }
      },
    },
    afterGap: TABLE_METRICS.GAP_AFTER_TEXT,
  });

  doc.setTextColor(...rgb(BRAND.MUTED));
  layout.drawTextLines(
    ['Note: Amounts in parentheses ( ) indicate debit entries - adjustments or refunds.'],
    {
      fontStyle: 'italic',
      fontSize: 7,
      lineHeight: TABLE_METRICS.LINE_HEIGHT,
      requiredHeight: TABLE_METRICS.LINE_HEIGHT + TABLE_METRICS.GAP_AFTER_SECTION,
      pageBreakReason: 'txn-footnote',
    },
  );

  const modeRows = modes.map((m) => [
    m.mode, String(m.count), fmtINR(m.total), `${m.percent.toFixed(1)}%`,
  ]);
  modeRows.push([
    'TOTAL',
    String(modes.reduce((a, b) => a + b.count, 0)),
    fmtINR(modes.reduce((a, b) => a + b.total, 0)),
    '100%',
  ]);

  layout.drawSectionTable({
    sectionId: 'payment-mode',
    drawTitleFn: (pdf, cursor) => drawSectionTitle(pdf, cursor, 3, 'Payment Mode Analysis'),
    rowCount: modeRows.length,
    tableOptions: {
      head: [['Mode', 'Transactions', 'Total Received', '% Share']],
      body: modeRows,
      theme: 'grid',
      headStyles: { ...goldTableHead, fontSize: 8.5 },
      bodyStyles: { fontSize: 8.5, cellPadding: 2.2 },
      alternateRowStyles: { fillColor: rgb(BRAND.ROW_ALT) },
      columnStyles: {
        0: { cellWidth: 40 },
        1: { halign: 'right', cellWidth: 30 },
        2: { halign: 'right', cellWidth: 35 },
        3: { halign: 'right', cellWidth: 25 },
      },
      didParseCell: (data) => {
        if (data.row.index === modeRows.length - 1) {
          data.cell.styles.fillColor = rgb(BRAND.GOLD_LIGHT);
          data.cell.styles.fontStyle = 'bold';
        }
      },
    },
  });

  const riskMap = {
    '0-30': 'Low',
    '31-60': 'Low',
    '61-90': 'Medium',
    '90+': 'High',
  };
  const riskColor = {
    Low: BRAND.EMERALD,
    Medium: BRAND.AMBER,
    High: BRAND.RUBY,
    '—': BRAND.MUTED,
  };
  const agingRows = aging.map((a) => [
    a.label === '90+' ? '>90 days' : `${a.label} days`,
    String(a.count),
    fmtINR(a.amount),
    a.amount > 0 ? riskMap[a.label] : '—',
  ]);
  agingRows.push([
    'TOTAL',
    String(aging.reduce((a, b) => a + b.count, 0)),
    fmtINR(aging.reduce((a, b) => a + b.amount, 0)),
    '',
  ]);

  layout.drawSectionTable({
    sectionId: 'aging',
    drawTitleFn: (pdf, cursor) => drawSectionTitle(pdf, cursor, 4, 'Outstanding Advance Aging'),
    rowCount: agingRows.length,
    tableOptions: {
      head: [['Bucket', 'Clients', 'Amount', 'Risk']],
      body: agingRows,
      theme: 'grid',
      headStyles: { ...goldTableHead, fontSize: 8.5 },
      bodyStyles: { fontSize: 8.5, cellPadding: 2.2 },
      alternateRowStyles: { fillColor: rgb(BRAND.ROW_ALT) },
      columnStyles: {
        0: { cellWidth: 35 },
        1: { halign: 'right', cellWidth: 25 },
        2: { halign: 'right', cellWidth: 35 },
        3: { halign: 'center', cellWidth: 25, fontStyle: 'bold' },
      },
      didParseCell: (data) => {
        if (data.row.index === agingRows.length - 1) {
          data.cell.styles.fillColor = rgb(BRAND.GOLD_LIGHT);
          data.cell.styles.fontStyle = 'bold';
        }
        if (data.column.index === 3 && data.section === 'body'
            && data.row.index < agingRows.length - 1) {
          const risk = data.cell.raw;
          if (riskColor[risk]) {
            data.cell.styles.textColor = rgb(riskColor[risk]);
          }
        }
      },
    },
  });

  layout.drawBlock(TABLE_METRICS.SIGNOFF_BLOCK, 'sign-off', (pdf, y) => {
    const headerY = drawSectionTitle(pdf, y, 5, 'Sign-off');
    const sigY = headerY + 4;
    const sigW = (pageWidth - 30 - 10) / 3;
    const sigBlocks = [
      { role: 'Prepared by', name: 'Development Express Engine', title: 'Auto-generated' },
      { role: 'Reviewed by', name: 'Finance Lead', title: 'Development Express' },
      { role: 'Approved by', name: 'Om Chavan', title: 'Managing Director' },
    ];
    sigBlocks.forEach((s, i) => {
      const x = LEDGER_LAYOUT.SIDE + i * (sigW + 5);
      pdf.setTextColor(...rgb(BRAND.GOLD_DARK));
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(7.5);
      pdf.text(s.role.toUpperCase(), x, sigY);
      pdf.setDrawColor(...rgb(BRAND.INK));
      pdf.setLineWidth(0.3);
      pdf.line(x, sigY + 18, x + sigW, sigY + 18);
      pdf.setTextColor(...rgb(BRAND.INK));
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(8);
      pdf.text(s.name, x, sigY + 23);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(7);
      pdf.setTextColor(...rgb(BRAND.MUTED));
      pdf.text(s.title, x, sigY + 27);
    });
    return sigY + TABLE_METRICS.SIGNOFF_BLOCK;
  });

  stampAllPages(doc, drawHeaderFooter);
  return doc;
};
